import {
  ResponsiveImage,
  EntityCardSize,
  UserContext,
  AppInsightsContext,
  TrackEventName,
  TrackEventKey,
  TrackEventType,
  Modal,
} from '@/components';
import { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import ImageIcon from '@/assets/imageIcon.svg';
import XLight from '@/assets/xLight.svg';
import EyeIcon from '../../assets/eye-light.svg';
import ReportButton from '../common/ReportButton';
import { useTranslations } from 'next-intl';
import { ReportCardModel } from './ReportCard';
import { getGlobalDataPdf } from '@/api';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import GDLogo from '@/assets/gd-logo.svg';

const ReportCardGD = ({
  data,
  className,
  size,
  onClick,
  hideTitle,
  hideTopContent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hideSaveToScrapbook,
  ...props
}: ReportCardModel) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const t = useTranslations();
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);

  const isImage = useMemo(() => !!data.image?.imageUrl, [data.image]);

  const imageSize = useMemo(() => {
    let widths: { [x: string]: number } = {};
    let heights: { [x: string]: number } = {};

    if (size === EntityCardSize.SMALL) {
      widths = { xs: 246, sm: 272, md: 272, lg: 366, xl: 366 };
      heights = { xs: 218, sm: 240, md: 240, lg: 326, xl: 326 };
    } else {
      widths = { xs: 550, sm: 550, md: 550, lg: 720, xl: 720 };
      heights = { xs: 490, sm: 490, md: 490, lg: 641, xl: 641 };
    }

    return { widths, heights };
  }, [size]);

  const closeDialog = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setDialogOpen(false);
    setPdfUrl('');
    /*
    pushValuesToRouteQuery(router, {
      pdfid: null,
    });
    */
  };

  const reportPdfFileName = useMemo(() => {
    try {
      const reportURLSearchParams = new URLSearchParams(new URL(data.reportUrl.href).search);
      return reportURLSearchParams.get('apiQueryParams');
    } catch (error) {
      console.error('Invalid report URL:', data.reportUrl.href, error);
      return null;
    }
  }, [data.reportUrl.href]);

  const viewPdf = async () => {
    if (!reportPdfFileName) return;

    setDialogOpen(true);
    const response = await getGlobalDataPdf(reportPdfFileName);
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    setPdfUrl(url + '#page=1');

    appInsights.trackCustomEvent(TrackEventName.GLOBAL_DATA_INTEGRATION, TrackEventKey.PDF, TrackEventType.OPEN, user, {
      reportId: data.reportId,
      title: data.title,
    });
    /*
      pushValuesToRouteQuery(router, {
        pdfid: uuidv4(),
      });
      */
  };

  const downloadPdf = async () => {
    if (!reportPdfFileName) return;

    try {
      const response = await getGlobalDataPdf(reportPdfFileName);
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.title + '.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      appInsights.trackCustomEvent(
        TrackEventName.GLOBAL_DATA_INTEGRATION,
        TrackEventKey.PDF,
        TrackEventType.DOWNLOAD,
        user,
        {
          reportId: data.reportId,
          title: data.title,
        },
      );
    } catch (error) {
      console.error('PDF download failed:', error);
    }
  };

  const _onClick = (e: React.MouseEvent) => {
    onClick?.(e, data);
  };

  return (
    <>
      <div
        className={classNames(
          'report-card-container overflow-hidden',
          size === EntityCardSize.LARGE
            ? 'xxl:h-[568px] xxl:w-[640px] h-[335px] w-[335px] lg:h-[400px] lg:w-[400px] xl:h-[497px] xl:w-[560px]'
            : size === EntityCardSize.SMALL
              ? 'h-[218px] w-[246px] sm:h-[240px] sm:w-[272px] xl:h-[326px] xl:w-[366px]'
              : 'aspect-[456/406] w-full',
          size,
          data.isLoading ? 'loading' : 'loaded',
          className,
        )}
        {...props}
      >
        {data.isLoading ? (
          <div className="flex h-full w-full animate-pulse items-center justify-center bg-gray-300">
            <ImageIcon className="h-10 w-10 text-gray-200 dark:text-gray-600" />
          </div>
        ) : (
          <div className="bg-gray-2 group relative flex h-full flex-col">
            <div className="flex h-full flex-col" onClick={_onClick}>
              {!hideTopContent && (
                <div
                  className={classNames(
                    'report-card pointer-events-none flex h-full grow flex-col bg-[#f9f5e5] text-[#1d232d]',
                  )}
                >
                  <div
                    className={classNames(
                      'pointer-events-auto flex items-center justify-between p-2 leading-[18px] font-bold lg:p-3 xl:p-4 xl:pb-2',
                      size !== EntityCardSize.LARGE && 'text-xs leading-5',
                    )}
                  >
                    <div className="flex">
                      <span>{data.directory?.name || 'GlobalData'}</span>
                    </div>
                    <span className="nowrap hidden xl:inline-block">
                      {dayjs(data.publishedDate)?.format('DD MMMM YYYY')}
                    </span>
                    <span className="nowrap xl:hidden">
                      {dayjs(data.publishedDate)?.format(size === EntityCardSize.LARGE ? 'DD MMMM YYYY' : 'YYYY')}
                    </span>
                  </div>
                  {!hideTitle && (
                    <div className="title xl-pt-0 flex h-full grow items-start p-2 pt-0 lg:p-3 lg:pt-0 xl:p-4 xl:pt-0">
                      <div
                        className={classNames(
                          'font-normal',
                          isImage
                            ? 'line-clamp-3 text-2xl leading-7 tracking-[0.1px] md:text-xl md:leading-6 lg:line-clamp-2 xl:text-xl xl:leading-[26px]'
                            : 'line-clamp-5 pr-10 text-4xl leading-11 tracking-[0.1px] md:pr-12 md:text-[28px] md:leading-9',
                        )}
                      >
                        {data.title}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {isImage && data.image && (
                <ResponsiveImage
                  image={data.image}
                  widths={imageSize.widths}
                  heights={imageSize.heights}
                  cdnProps={{ mode: 'crop' }}
                  imgProps={{ className: 'aspect-video' }}
                />
              )}
            </div>

            {/* Action Buttons - Hover Only */}
            {(user.isAuthenticated || user.isIpBasedIdentity) && (
              <div className="buttons pointer-events-none absolute bottom-0 flex w-full justify-end opacity-0 transition-opacity duration-500 group-hover:opacity-100 pointer-coarse:opacity-100">
                <div className="button-list mr-2 mb-2">
                  <div className="flex justify-end">
                    <ReportButton
                      icon={EyeIcon}
                      text={t('general.$viewPdf')}
                      onClick={viewPdf}
                      textClassName="pr-5"
                      dark={!isImage}
                    />
                  </div>
                  <div className="flex justify-end">
                    <ReportButton
                      icon={DownloadSimpleLight}
                      text={t('general.$downloadPdf')}
                      onClick={downloadPdf}
                      textClassName="pr-5"
                      dark={!isImage}
                    />
                  </div>
                </div>
              </div>
            )}

            <GDLogo
              className={classNames('absolute bottom-3 left-3 h-11 w-11', isImage ? 'fill-white' : 'fill-[#252628]')}
            />
          </div>
        )}
      </div>
      <Modal open={dialogOpen} onClose={closeDialog} contanerClassName="w-[90vw] h-[90vh] bg-white shadow-xl">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-300 p-4">
            <h2 className="text-lg font-medium">{data.title}</h2>
            <button onClick={closeDialog} className="btn icon">
              <XLight className="h-7 w-7" />
            </button>
          </div>
          <div className="flex-1 overflow-hidden p-4">
            {pdfUrl && <object data={pdfUrl} type="application/pdf" className="h-full w-full"></object>}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReportCardGD;
